import React, { useState, useEffect, useMemo } from 'react';
import { Autocomplete, Button, Grid, TextField, Typography, Tooltip, Box } from '@mui/material';
import ApiRequestService from '../../component/ApiRequestService';


const SubjectTopicCategorySelect = ({ onAddSubcategories }) => {

    const [subjects, setSubjects] = useState([]);
    const [topics, setTopics] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([])

    const [selectedSubject, setSelectedSubject] = useState(null);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSubcategories, setSelectedSubcategories] = useState([]);

    const mappedSubcategories = useMemo(() => {
        return subcategories?.map(subcategory => ({
            id: subcategory.subcategory_id,
            label: subcategory.subcategory_name,
            data: subcategory,
        })) || [];
    }, [subcategories]);

    useEffect(() => {
        getSubjects();
    }, [])

    const getSubjects = () => {
        ApiRequestService({
            method: 'post',
            url: '/getAllSubjects'
        }).then(res => {
            if (res && res.data && res.data.data) {
                let subjectsArr = res.data.data.subject_names.filter(subj => subj.sync_validation);
                setSubjects(subjectsArr);
            }
        })
    }

    const getTopics = (payload) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllTopics',
            data: payload
        }).then(res => {
            if (res && res.data && res.data.data) {
                setTopics(res.data.data.topic_names);
            }
        })
    }

    const getCategories = (payload) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllCategories',
            data: payload
        }).then(res => {
            if (res && res.data && res.data.data) {
                setCategories(res.data.data.category_names);
            }
        })
    }

    const getSubcategories = (payload) => {
        ApiRequestService({
            method: 'post',
            url: '/getAllSubCategories',
            data: payload
        }).then(res => {
            if (res && res.data && res.data.data) {
                setSubcategories(res.data.data.subCategory_names);
            }
        })
    }


    const handleSubjectSelect = (_, value) => {
        setTopics(null);
        setCategories(null);
        setSubcategories(null);
        setSelectedSubject(null);
        setSelectedTopic(null);
        setSelectedCategory(null);
        setSelectedSubcategories(null);
        if (value == null) return;
        setSelectedSubject(value);
        getTopics({
            subject_id: value.id
        })
    }

    const handleTopicSelect = (_, value) => {
        setCategories(null);
        setSubcategories(null);

        setSelectedTopic(null);
        setSelectedCategory(null);
        setSelectedSubcategories(null);
        if (value == null) return;
        setSelectedTopic(value);
        getCategories({
            subject_id: value.data.subject_id,
            topic_id: value.data.topic_id
        })
    }

    const handleCategorySelect = (_, value) => {
        setSubcategories(null);
        setSelectedCategory(null);
        setSelectedSubcategories(null);
        if (value == null) return;
        setSelectedCategory(value);

        getSubcategories({
            subject_id: value.data.subject_id,
            topic_id: value.data.topic_id,
            category_id: value.data.category_id,
        })
    }

    const handleSubcategoriesSelect = (_, values) => {
        setSelectedSubcategories(null);
        if (values == null) return;
        let newSubcats = [...values];
        setSelectedSubcategories(newSubcats);
    }

    const handleSelectAll = () => {
        setSelectedSubcategories([...mappedSubcategories]);
    }

    const handleRemoveAll = () => {
        setSelectedSubcategories([]);
    }

    const handleAddSubcategories = () => {
        const result = onAddSubcategories(selectedSubcategories, selectedSubject.label, selectedTopic.label, selectedCategory.label);
        if (result === false) return;
        // Clear selected category and subcategories after adding to table
        setSelectedCategory(null);
        setSelectedSubcategories([]);
        setSubcategories(null);
    }

    return (
        <Grid container spacing={2} alignItems={'start'}>
            <Grid item xs={2}>
                <Autocomplete
                    disablePortal
                    size='small'
                    options={subjects ? subjects.map(subject => {
                        return {
                            id: subject.subject_id,
                            label: subject.subject_name,
                            data: subject,
                        }
                    }) : []}
                    value={selectedSubject}
                    onChange={handleSubjectSelect}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Subject"
                            InputProps={{
                                ...params.InputProps,
                                sx: {
                                    borderRadius: "6px",
                                },
                            }}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={2}>
                <Autocomplete
                    disablePortal
                    size='small'
                    options={topics ? topics.map(topic => {
                        return {
                            id: topic.topic_id,
                            label: topic.topic_name,
                            data: topic,
                        }
                    }) : []}
                    value={selectedTopic}
                    onChange={handleTopicSelect}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Topic"
                            InputProps={{
                                ...params.InputProps,
                                sx: {
                                    borderRadius: "6px",
                                },
                            }}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={2}>
                <Autocomplete
                    disablePortal
                    size='small'
                    options={categories ? categories.map(category => {
                        return {
                            id: category.category_id,
                            label: category.category_name,
                            data: category,
                        }
                    }) : []}
                    value={selectedCategory}
                    onChange={handleCategorySelect}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Category"
                            InputProps={{
                                ...params.InputProps,
                                sx: {
                                    borderRadius: "6px",
                                },
                            }}
                        />
                    )}
                />
            </Grid>
            <Grid item xs={4}>
                <Autocomplete
                    disablePortal
                    size='small'
                    options={mappedSubcategories}
                    multiple
                    disableCloseOnSelect
                    filterSelectedOptions
                    value={selectedSubcategories ? selectedSubcategories : []}
                    onChange={handleSubcategoriesSelect}
                    getOptionLabel={(option) => option.label}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Select Subcategories"
                            InputProps={{
                                ...params.InputProps,
                                sx: {
                                    borderRadius: "6px",
                                },
                            }}
                        />
                    )}
                    ListboxComponent={(props) => (
                        <Box>
                            <Box sx={{ display: 'flex', gap: 1, p: 1, borderBottom: '1px solid #e0e0e0' }}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    fullWidth
                                    disabled={!mappedSubcategories.length || selectedSubcategories?.length === mappedSubcategories.length}
                                    onMouseDown={(e) => { e.preventDefault(); handleSelectAll(); }}
                                >
                                    Select All
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    fullWidth
                                    disabled={!selectedSubcategories?.length}
                                    onMouseDown={(e) => { e.preventDefault(); handleRemoveAll(); }}
                                >
                                    Remove All
                                </Button>
                            </Box>
                            <ul {...props} />
                        </Box>
                    )}
                />
            </Grid>
            <Grid item xs={2}>
                <Tooltip
                    arrow
                    disableHoverListener={selectedSubcategories && selectedSubcategories.length > 0}
                    title={
                        <Typography className="text-white" fontSize={14}>
                            Please select one or more categories to add to the table.
                        </Typography>
                    }
                >
                    <span>
                        <Button
                            variant="contained"
                            onClick={handleAddSubcategories}
                            disabled={!selectedSubcategories || selectedSubcategories.length === 0}
                        >
                            {`Add Subcategor${selectedSubcategories && selectedSubcategories.length > 1 ? 'ies' : 'y'} to table`}
                        </Button>
                    </span>
                </Tooltip>
            </Grid>
        </Grid>
    );
}

export default SubjectTopicCategorySelect;
